import { Injectable, signal, computed } from "@angular/core";
import { VENDOR_DELIVERY_FILTER_OPTIONS } from "../../../../../domains/vendors/models/JobIssueVendorDTO";
import { VENDOR_DELIVERY_FILTER_LABELS } from "../../open-items/open-items-list/services/filter.service";
import { FilterMode, FilterValue, FilterCategory } from "./filter-panel.component";

export const BQ_STEPS = {
  BQ6: "BQ6",
  BQ7: "BQ7",
  BQ8: "BQ8"
} as const;

export interface FilterState {
  isOpen: boolean;
  mode: FilterMode;
  currentType: string | null;
  values: FilterValue[];
  selected: { [key: string]: string[] };
  original: { [key: string]: string[] };
  originalCategory: string[];
}

@Injectable({
  providedIn: "root"
})
export class FilterService {
  // Filter state
  private filterState = signal<FilterState>({
    isOpen: false,
    mode: "selection",
    currentType: null,
    values: [],
    selected: {},
    original: {},
    originalCategory: []
  });

  // Public readonly signals
  readonly isOpen = computed(() => this.filterState().isOpen);
  readonly mode = computed(() => this.filterState().mode);
  readonly currentType = computed(() => this.filterState().currentType);
  readonly values = computed(() => this.filterState().values);
  readonly selected = computed(() => this.filterState().selected);
  readonly original = computed(() => this.filterState().original);
  readonly originalCategory = computed(() => this.filterState().originalCategory);

  // Computed properties
  readonly selectedCount = computed(() => {
    const selected = this.selected();
    return Object.values(selected).reduce((total, filters) => total + filters.length, 0);
  });

  readonly hasActiveFilters = computed(() => {
    const selected = this.selected();
    return Object.values(selected).some(filters => filters.length > 0);
  });

  // Filter options
  readonly FilterCategories: FilterCategory[] = [
    { id: "bq-step", label: "BQ Step", route: "/bq-step" },
    { id: "room", label: "Room", route: "/room" },
    { id: "status", label: "Status", route: "/status" },
    { id: "trade-partner", label: "Trade Partner", route: "/trade-partner" },
  ];

  // Status options using constants for type safety
  readonly BQStepOptions: string[] = [
    BQ_STEPS.BQ6,
    BQ_STEPS.BQ7,
    BQ_STEPS.BQ8
  ];

  // Status options using constants for type safety
  readonly statusOptions = VENDOR_DELIVERY_FILTER_OPTIONS;

  // State management methods
  openFilterDrawer(): void {
    this.updateState({
      original: { ...this.selected() },
      isOpen: true,
      mode: "selection"
    });
  }

  closeFilterDrawer(): boolean {
    const hasActiveFilters = this.hasActiveFilters();

    this.updateState({
      isOpen: false,
      mode: "selection",
      currentType: null,
      values: [],
      originalCategory: []
    });

    return hasActiveFilters;
  }

  setMode(mode: FilterMode): void {
    this.updateState({ mode });
  }

  setCurrentType(type: string | null): void {
    this.updateState({ currentType: type });
  }

  setValues(values: FilterValue[]): void {
    this.updateState({ values });
  }

  setSelected(selected: { [key: string]: string[] }): void {
    this.updateState({ selected });
  }

  clearAllFilters(): void {
    this.updateState({
      selected: {
        "trade-partner": [],
        "room": [],
        "status": [],
        "bq-step": []
      }
    });
  }

  // Category-specific methods
  enterConfigurationMode(option: FilterCategory, selectedValues: string[]): void {
    this.updateState({
      currentType: option.label,
      mode: "configuration",
      originalCategory: [...selectedValues]
    });
  }

  exitConfigurationMode(action: "select" | "cancel"): { [key: string]: string[] } {
    const currentType = this.currentType();
    const filterTypeId = this.FilterCategories.find(opt => opt.label === currentType)?.id;

    if (!filterTypeId) {
      return this.selected();
    }

    const currentSelected = this.selected();

    if (action === "select") {
      const selectedValues = this.values()
        .filter(v => v.selected)
        .map(v => v.id);

      return {
        ...currentSelected,
        [filterTypeId]: selectedValues
      };
    }

    return {
      ...currentSelected,
      [filterTypeId]: [...this.originalCategory()]
    };
  }

  // Filter value management
  toggleFilterValue(value: FilterValue): void {
    const currentValues = this.values();
    const updatedValues = currentValues.map(v => {
      if (v.id === value.id) {
        return { ...v, selected: !v.selected };
      }
      return v;
    });
    this.setValues(updatedValues);
  }

  removeFilterPill(filterType: string, filterId: string): void {
    const currentSelected = this.selected();
    const currentValues = currentSelected[filterType] || [];
    const updatedValues = currentValues.filter(id => id !== filterId);

    this.setSelected({
      ...currentSelected,
      [filterType]: updatedValues
    });
  }

  // Utility methods
  private updateState(updates: Partial<FilterState>): void {
    this.filterState.update(current => ({ ...current, ...updates }));
  }

  // Reset to original state
  resetToOriginal(): void {
    this.updateState({
      selected: { ...this.original() }
    });
  }

  // Generate filter values for a specific option
  generateFilterValues(
    option: FilterCategory,
    availableValues: string[],
    selectedValues: string[]
  ): FilterValue[] {
    return availableValues.map(value => ({
      id: value,
      label: value,
      selected: selectedValues.includes(value)
    }));
  }

  // Strategy pattern for filter value generation
  generateFilterValuesForCategory(
    option: FilterCategory,
    selectedValues: string[],
    dataAccessors: {
      getTradePartners: () => string[];
      getAreas: () => string[];
    }
  ): FilterValue[] {
    const strategies: Record<string, () => string[]> = {
      "trade-partner": () => dataAccessors.getTradePartners(),
      "room": () => dataAccessors.getAreas(),
      "status": () => this.statusOptions,
      "bq-step": () => this.BQStepOptions
    };

    const getAvailableValues = strategies[option.id];
    if (!getAvailableValues) {
      console.warn(`No strategy found for filter category: ${option.id}`);
      return [];
    }

    const availableValues = getAvailableValues();

    if (option.id === "status") {
      return availableValues.map(value => ({
        id: value,
        label: VENDOR_DELIVERY_FILTER_LABELS[value as keyof typeof VENDOR_DELIVERY_FILTER_LABELS] ?? value,
        selected: selectedValues.includes(value)
      }));
    }

    return this.generateFilterValues(
      option,
      availableValues,
      selectedValues
    );
  }

  // Computed property for selected filters display
  selectedFiltersDisplay = computed(() => {
    const selected = this.selected();
    const result: { [key: string]: { id: string; label: string; type: string }[] } = {
      "trade-partner": [],
      "room": [],
      "status": [],
      "bq-step": []
    };

    // Trade Partners
    if (selected["trade-partner"] && selected["trade-partner"].length > 0) {
      result["trade-partner"] = selected["trade-partner"].map(name => ({
        id: name,
        label: name,
        type: "trade-partner"
      }));
    }

    // Rooms
    if (selected["room"] && selected["room"].length > 0) {
      result["room"] = selected["room"].map(area => ({
        id: area,
        label: area,
        type: "room"
      }));
    }

    // Status
    if (selected["status"] && selected["status"].length > 0) {
      result["status"] = selected["status"].map(status => ({
        id: status,
        label: VENDOR_DELIVERY_FILTER_LABELS[status as keyof typeof VENDOR_DELIVERY_FILTER_LABELS] ?? status,
        type: "status"
      }));
    }

    // Status
    if (selected["bq-step"] && selected["bq-step"].length > 0) {
      result["bq-step"] = selected["bq-step"].map(bqStep => ({
        id: bqStep,
        label: bqStep,
        type: "bq-step"
      }));
    }

    return result;
  });

  // Convert selectedFiltersDisplay object to array for iteration
  getSelectedFiltersAsArray(): [string, { id: string; label: string; type: string }[]][] {
    const selected = this.selectedFiltersDisplay();
    return Object.entries(selected).filter(([_, filters]) => filters.length > 0);
  }

  // Button state computations
  isSelectButtonDisabled = computed(() =>
    (this.currentType() === "Trade Partner" && this.values().length === 0) ||
    this.values().filter(v => v.selected).length === 0
  );

  isApplyButtonDisabled = computed(() =>
    this.selectedCount() === 0
  );
}
